import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {
  TrophyIcon,
  ChartBarIcon,
  ArrowTrendingUpIcon,
  UserGroupIcon,
  FireIcon,
  StarIcon,
  CurrencyDollarIcon,
  SparklesIcon,
  CheckCircleIcon,
} from '@heroicons/react/24/outline';
import { Link } from 'react-router-dom';

interface Trader {
  rank: number;
  address: string;
  username: string;
  avatar: string;
  verified: boolean;
  tier: 'bronze' | 'silver' | 'gold' | 'platinum';
  pnl: number;
  pnlPercent: number;
  volume: number;
  trades: number;
  winRate: number;
  followers: number;
  following: boolean;
}

const mockTraders: Trader[] = [
  {
    rank: 1,
    address: '0x1234...5678',
    username: 'NFTWhale',
    avatar: 'https://picsum.photos/seed/trader1/100/100',
    verified: true,
    tier: 'platinum',
    pnl: 1245.8,
    pnlPercent: 156.3,
    volume: 8920.5,
    trades: 342,
    winRate: 78,
    followers: 12450,
    following: false,
  },
  {
    rank: 2,
    address: '0xABCD...EFGH',
    username: 'CryptoKing',
    avatar: 'https://picsum.photos/seed/trader2/100/100',
    verified: true,
    tier: 'platinum',
    pnl: 987.2,
    pnlPercent: 124.7,
    volume: 6540.3,
    trades: 287,
    winRate: 72,
    followers: 9823,
    following: true,
  },
  {
    rank: 3,
    address: '0x9876...5432',
    username: 'ArtCollector',
    avatar: 'https://picsum.photos/seed/trader3/100/100',
    verified: true,
    tier: 'gold',
    pnl: 756.9,
    pnlPercent: 98.4,
    volume: 4320.8,
    trades: 198,
    winRate: 68,
    followers: 7654,
    following: false,
  },
  {
    rank: 4,
    address: '0x5555...6666',
    username: 'DiamondHands',
    avatar: 'https://picsum.photos/seed/trader4/100/100',
    verified: false,
    tier: 'gold',
    pnl: 623.4,
    pnlPercent: 87.2,
    volume: 3890.2,
    trades: 165,
    winRate: 65,
    followers: 5432,
    following: false,
  },
  {
    rank: 5,
    address: '0x7777...8888',
    username: 'FloorSweeper',
    avatar: 'https://picsum.photos/seed/trader5/100/100',
    verified: true,
    tier: 'silver',
    pnl: 489.1,
    pnlPercent: 76.8,
    volume: 2780.6,
    trades: 234,
    winRate: 62,
    followers: 4321,
    following: true,
  },
  {
    rank: 6,
    address: '0x2222...3333',
    username: 'BullishBob',
    avatar: 'https://picsum.photos/seed/trader6/100/100',
    verified: false,
    tier: 'silver',
    pnl: 378.5,
    pnlPercent: 65.3,
    volume: 2340.1,
    trades: 156,
    winRate: 59,
    followers: 3210,
    following: false,
  },
  {
    rank: 7,
    address: '0x4444...5555',
    username: 'NFTNinja',
    avatar: 'https://picsum.photos/seed/trader7/100/100',
    verified: true,
    tier: 'silver',
    pnl: 312.8,
    pnlPercent: 54.2,
    volume: 1980.4,
    trades: 189,
    winRate: 57,
    followers: 2890,
    following: false,
  },
  {
    rank: 8,
    address: '0x6666...7777',
    username: 'CryptoQueen',
    avatar: 'https://picsum.photos/seed/trader8/100/100',
    verified: true,
    tier: 'bronze',
    pnl: 256.3,
    pnlPercent: 45.8,
    volume: 1560.2,
    trades: 134,
    winRate: 55,
    followers: 2345,
    following: false,
  },
  {
    rank: 9,
    address: '0x8888...9999',
    username: 'PixelPunker',
    avatar: 'https://picsum.photos/seed/trader9/100/100',
    verified: false,
    tier: 'bronze',
    pnl: 198.7,
    pnlPercent: 38.4,
    volume: 1230.8,
    trades: 98,
    winRate: 52,
    followers: 1876,
    following: true,
  },
  {
    rank: 10,
    address: '0xAAAA...BBBB',
    username: 'DegenDave',
    avatar: 'https://picsum.photos/seed/trader10/100/100',
    verified: false,
    tier: 'bronze',
    pnl: 156.2,
    pnlPercent: 32.1,
    volume: 980.5,
    trades: 76,
    winRate: 48,
    followers: 1234,
    following: false,
  },
];

const tierColors = {
  bronze: 'from-amber-700 to-amber-900',
  silver: 'from-gray-400 to-gray-600',
  gold: 'from-yellow-400 to-amber-600',
  platinum: 'from-slate-200 to-slate-400',
};

const LeaderboardPage: React.FC = () => {
  const [timeRange, setTimeRange] = useState('7d');
  const [category, setCategory] = useState<'pnl' | 'volume' | 'followers'>('pnl');

  const formatNumber = (num: number) => {
    if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
    return num.toFixed(1);
  };

  const getRankIcon = (rank: number) => {
    if (rank === 1) return <TrophyIcon className="h-6 w-6 text-yellow-400" />;
    if (rank === 2) return <TrophyIcon className="h-6 w-6 text-gray-400" />;
    if (rank === 3) return <TrophyIcon className="h-6 w-6 text-amber-600" />;
    return <span className="text-lg font-bold text-gray-500">{rank}</span>;
  };

  // Sort traders based on category
  const sortedTraders = [...mockTraders].sort((a, b) => {
    if (category === 'pnl') return b.pnl - a.pnl;
    if (category === 'volume') return b.volume - a.volume;
    return b.followers - a.followers;
  });

  return (
    <div className="min-h-screen py-8">
      <div className="container-custom">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-12"
        >
          <h1 className="text-4xl md:text-5xl font-bold mb-4">
            <span className="text-gradient">Social Trading Leaderboard</span>
          </h1>
          <p className="text-gray-400 text-lg max-w-2xl">
            Discover top traders, follow their strategies, and enable copy trading to mirror their moves
          </p>
        </motion.div>

        {/* Top 3 Podium */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="mb-12"
        >
          <div className="flex items-end justify-center gap-4 md:gap-8">
            {/* Second Place */}
            <div className="order-1 flex-1 max-w-xs">
              <div className="glass-card p-6 text-center relative">
                <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                  <div className="w-10 h-10 rounded-full bg-gray-400 flex items-center justify-center">
                    <span className="text-lg font-bold text-dark-900">2</span>
                  </div>
                </div>
                <img
                  src={sortedTraders[1]?.avatar}
                  alt={sortedTraders[1]?.username}
                  className="w-20 h-20 rounded-full mx-auto mb-4 border-4 border-gray-400"
                />
                <h3 className="font-semibold text-lg flex items-center justify-center space-x-1">
                  <span>{sortedTraders[1]?.username}</span>
                  {sortedTraders[1]?.verified && <CheckCircleIcon className="h-4 w-4 text-primary-400" />}
                </h3>
                <p className="text-2xl font-bold text-green-400 mt-2">
                  +{sortedTraders[1]?.pnl} ETH
                </p>
                <p className="text-sm text-gray-400">
                  {sortedTraders[1]?.followers.toLocaleString()} followers
                </p>
              </div>
              <div className="h-24 bg-gradient-to-t from-gray-600/20 to-gray-400/20 rounded-b-xl" />
            </div>

            {/* First Place */}
            <div className="order-2 flex-1 max-w-xs -mt-8">
              <div className="glass-card p-8 text-center relative border-yellow-500/50">
                <div className="absolute -top-6 left-1/2 transform -translate-x-1/2">
                  <div className="w-14 h-14 rounded-full bg-gradient-to-r from-yellow-400 to-amber-600 flex items-center justify-center shadow-lg shadow-yellow-500/30">
                    <TrophyIcon className="h-8 w-8 text-white" />
                  </div>
                </div>
                <img
                  src={sortedTraders[0]?.avatar}
                  alt={sortedTraders[0]?.username}
                  className="w-24 h-24 rounded-full mx-auto mb-4 border-4 border-yellow-400"
                />
                <h3 className="font-semibold text-xl flex items-center justify-center space-x-1">
                  <span>{sortedTraders[0]?.username}</span>
                  {sortedTraders[0]?.verified && <CheckCircleIcon className="h-5 w-5 text-primary-400" />}
                </h3>
                <p className="text-3xl font-bold text-green-400 mt-2">
                  +{sortedTraders[0]?.pnl} ETH
                </p>
                <p className="text-sm text-gray-400 mb-4">
                  {sortedTraders[0]?.followers.toLocaleString()} followers
                </p>
                <button className="btn-primary w-full">
                  Copy Trade
                </button>
              </div>
              <div className="h-32 bg-gradient-to-t from-yellow-600/20 to-yellow-400/20 rounded-b-xl" />
            </div>

            {/* Third Place */}
            <div className="order-3 flex-1 max-w-xs">
              <div className="glass-card p-6 text-center relative">
                <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                  <div className="w-10 h-10 rounded-full bg-amber-600 flex items-center justify-center">
                    <span className="text-lg font-bold text-white">3</span>
                  </div>
                </div>
                <img
                  src={sortedTraders[2]?.avatar}
                  alt={sortedTraders[2]?.username}
                  className="w-20 h-20 rounded-full mx-auto mb-4 border-4 border-amber-600"
                />
                <h3 className="font-semibold text-lg flex items-center justify-center space-x-1">
                  <span>{sortedTraders[2]?.username}</span>
                  {sortedTraders[2]?.verified && <CheckCircleIcon className="h-4 w-4 text-primary-400" />}
                </h3>
                <p className="text-2xl font-bold text-green-400 mt-2">
                  +{sortedTraders[2]?.pnl} ETH
                </p>
                <p className="text-sm text-gray-400">
                  {sortedTraders[2]?.followers.toLocaleString()} followers
                </p>
              </div>
              <div className="h-16 bg-gradient-to-t from-amber-700/20 to-amber-600/20 rounded-b-xl" />
            </div>
          </div>
        </motion.div>

        {/* Filters */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="flex flex-col md:flex-row items-center justify-between gap-4 mb-8"
        >
          {/* Time Range */}
          <div className="flex items-center space-x-2 bg-dark-800/50 border border-white/10 rounded-xl p-1">
            {['24h', '7d', '30d', 'All'].map((range) => (
              <button
                key={range}
                onClick={() => setTimeRange(range)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  timeRange === range
                    ? 'bg-primary-500 text-white'
                    : 'text-gray-400 hover:text-white'
                }`}
              >
                {range}
              </button>
            ))}
          </div>

          {/* Category */}
          <div className="flex items-center space-x-4">
            {[
              { id: 'pnl', label: 'Top PnL', icon: ArrowTrendingUpIcon },
              { id: 'volume', label: 'Top Volume', icon: ChartBarIcon },
              { id: 'followers', label: 'Most Followed', icon: UserGroupIcon },
            ].map((cat) => {
              const Icon = cat.icon;
              return (
                <button
                  key={cat.id}
                  onClick={() => setCategory(cat.id as any)}
                  className={`flex items-center space-x-2 px-4 py-2 rounded-xl transition-colors ${
                    category === cat.id
                      ? 'bg-primary-500/20 text-primary-400 border border-primary-500/50'
                      : 'bg-dark-800/50 text-gray-400 border border-white/10 hover:border-white/20'
                  }`}
                >
                  <Icon className="h-5 w-5" />
                  <span>{cat.label}</span>
                </button>
              );
            })}
          </div>
        </motion.div>

        {/* Leaderboard Table */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="glass-card overflow-hidden"
        >
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-white/10">
                  <th className="px-6 py-4 text-left text-sm font-medium text-gray-400">Rank</th>
                  <th className="px-6 py-4 text-left text-sm font-medium text-gray-400">Trader</th>
                  <th className="px-6 py-4 text-right text-sm font-medium text-gray-400">PnL</th>
                  <th className="px-6 py-4 text-right text-sm font-medium text-gray-400">Volume</th>
                  <th className="px-6 py-4 text-right text-sm font-medium text-gray-400">Trades</th>
                  <th className="px-6 py-4 text-right text-sm font-medium text-gray-400">Win Rate</th>
                  <th className="px-6 py-4 text-right text-sm font-medium text-gray-400">Followers</th>
                  <th className="px-6 py-4 text-center text-sm font-medium text-gray-400">Action</th>
                </tr>
              </thead>
              <tbody>
                {sortedTraders.slice(3).map((trader, index) => (
                  <tr
                    key={trader.address}
                    className="border-b border-white/5 hover:bg-white/5 transition-colors"
                  >
                    <td className="px-6 py-4">
                      <div className="flex items-center justify-center w-8 h-8">
                        {getRankIcon(trader.rank)}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <Link
                        to={`/profile/${trader.address}`}
                        className="flex items-center space-x-3 group"
                      >
                        <img
                          src={trader.avatar}
                          alt={trader.username}
                          className="w-10 h-10 rounded-full"
                        />
                        <div>
                          <div className="flex items-center space-x-2">
                            <span className="font-semibold group-hover:text-primary-400 transition-colors">
                              {trader.username}
                            </span>
                            {trader.verified && (
                              <CheckCircleIcon className="h-4 w-4 text-primary-400" />
                            )}
                            <span className={`px-2 py-0.5 rounded-full text-xs bg-gradient-to-r ${tierColors[trader.tier]} text-white capitalize`}>
                              {trader.tier}
                            </span>
                          </div>
                          <span className="text-sm text-gray-500">{trader.address}</span>
                        </div>
                      </Link>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div>
                        <span className="font-semibold text-green-400">+{trader.pnl} ETH</span>
                        <p className="text-xs text-gray-500">+{trader.pnlPercent}%</p>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-right font-semibold">
                      {formatNumber(trader.volume)} ETH
                    </td>
                    <td className="px-6 py-4 text-right text-gray-400">
                      {trader.trades}
                    </td>
                    <td className="px-6 py-4 text-right">
                      <span className={trader.winRate >= 60 ? 'text-green-400' : 'text-gray-400'}>
                        {trader.winRate}%
                      </span>
                    </td>
                    <td className="px-6 py-4 text-right text-gray-400">
                      {formatNumber(trader.followers)}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center justify-center space-x-2">
                        <button
                          className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                            trader.following
                              ? 'bg-primary-500/20 text-primary-400 border border-primary-500/50'
                              : 'bg-dark-700 text-gray-400 hover:text-white'
                          }`}
                        >
                          {trader.following ? 'Following' : 'Follow'}
                        </button>
                        <button className="px-3 py-1.5 bg-gradient-to-r from-primary-500 to-accent-500 text-white rounded-lg text-sm font-medium hover:opacity-90 transition-opacity">
                          Copy
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </motion.div>

        {/* Copy Trading Info */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6"
        >
          <div className="glass-card p-6 text-center">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-r from-primary-500 to-accent-500 flex items-center justify-center">
              <UserGroupIcon className="h-8 w-8 text-white" />
            </div>
            <h3 className="text-xl font-bold mb-2">Follow Top Traders</h3>
            <p className="text-gray-400 text-sm">
              Track successful traders and see their moves in real-time
            </p>
          </div>

          <div className="glass-card p-6 text-center">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-r from-green-500 to-emerald-500 flex items-center justify-center">
              <SparklesIcon className="h-8 w-8 text-white" />
            </div>
            <h3 className="text-xl font-bold mb-2">Enable Copy Trading</h3>
            <p className="text-gray-400 text-sm">
              Automatically mirror trades from your favorite traders
            </p>
          </div>

          <div className="glass-card p-6 text-center">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-r from-amber-500 to-orange-500 flex items-center justify-center">
              <FireIcon className="h-8 w-8 text-white" />
            </div>
            <h3 className="text-xl font-bold mb-2">Earn Reputation</h3>
            <p className="text-gray-400 text-sm">
              Build your reputation and earn rewards as followers copy you
            </p>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default LeaderboardPage;
